import React from "react"
import {Home, Zap, Wifi, CheckCircle} from 'lucide-react'

const GeneracPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-500 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Generac Standby Generator Auto Start Systems
            </h1>
            <p className="text-xl text-orange-50 leading-relaxed mb-4">
              Automatic transfer switch integration and remote monitoring solutions for Generac home standby and portable generators.
            </p>
            <p className="text-lg text-orange-100">
              America's #1 selling generator brand with factory-integrated auto start and advanced monitoring capabilities.
            </p>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Generac Generator Categories
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-orange-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Home Standby</h3>
              <p className="text-gray-600 mb-4">
                Permanently installed with automatic transfer switch for whole-home backup power.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Guardian Series (7.5-26kW)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Protector Series (15-60kW)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Built-in auto start with ATS
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Mobile Link WiFi monitoring
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-orange-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Portable w/ CO-Sense</h3>
              <p className="text-gray-600 mb-4">
                Portable generators with remote start capability and carbon monoxide shutdown.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  GP Series (3000-8500W)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  iQ Series (2000-3500W)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Optional wireless remote
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Electric start models
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-orange-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Industrial Mobile</h3>
              <p className="text-gray-600 mb-4">
                Diesel and natural gas units for commercial and construction applications.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Mobile Link (6-150kW)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Industrial Series (10-150kW)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Remote monitoring included
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-orange-500 mr-2" />
                  Auto transfer switch options
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Why Choose Generac Auto Start?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-orange-100 rounded-full mb-4">
                <Home size={32} className="text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Whole Home Backup</h3>
              <p className="text-gray-600">
                Automatic power restoration within seconds of outage detection
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-orange-100 rounded-full mb-4">
                <Zap size={32} className="text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Integration</h3>
              <p className="text-gray-600">
                Works with automatic transfer switches for seamless power switching
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-orange-100 rounded-full mb-4">
                <Wifi size={32} className="text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Mobile Link</h3>
              <p className="text-gray-600">
                Remote monitoring via smartphone app from anywhere
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-orange-100 rounded-full mb-4">
                <CheckCircle size={32} className="text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Weekly Exercise</h3>
              <p className="text-gray-600">
                Automatic self-testing keeps system ready when needed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Transfer Switch Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Automatic Transfer Switch (ATS) Integration
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Whole House Transfer Switch
              </h3>
              <p className="text-gray-700 mb-4">
                Automatically switches your entire home to generator power during outages, then back to utility when power returns.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>100A, 200A, or 400A service ratings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>10-second power restoration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>NEMA 3R outdoor rated enclosure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Load shedding for smaller generators</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Load Management Switch
              </h3>
              <p className="text-gray-700 mb-4">
                Smart load management prioritizes critical circuits, allowing smaller generators to power more appliances.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Priority circuit selection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Automatic load shedding</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Smaller generator requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Cost savings on installation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Link */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Mobile Link Remote Monitoring
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Real-Time Monitoring</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Wifi size={20} className="text-orange-500 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold">Generator Status</div>
                      <div className="text-sm text-gray-600">Current operating status, battery voltage, maintenance alerts</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Wifi size={20} className="text-orange-500 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold">Outage Notifications</div>
                      <div className="text-sm text-gray-600">Instant alerts when power fails and generator activates</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Wifi size={20} className="text-orange-500 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold">Service Scheduling</div>
                      <div className="text-sm text-gray-600">Maintenance reminders based on run hours</div>
                    </div>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Remote Control</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle size={20} className="text-orange-500 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold">Start/Stop Control</div>
                      <div className="text-sm text-gray-600">Manual generator control from smartphone</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={20} className="text-orange-500 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold">Exercise Scheduling</div>
                      <div className="text-sm text-gray-600">Adjust weekly test run schedule remotely</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={20} className="text-orange-500 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold">Dealer Support Access</div>
                      <div className="text-sm text-gray-600">Grant service technician remote diagnostics</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
            <p className="text-gray-700">
              <strong>Note:</strong> Mobile Link requires WiFi connection and annual subscription ($99/year) after initial 1-year free trial period.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Protect Your Home with Generac
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Learn more about Generac automatic standby generators and transfer switch installation.
          </p>
        </div>
      </section>
    </div>
  )
}

export default GeneracPage